'use client';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { getDesignTitle, getDesignSupportEmail } from '@/utils/api';

// Read persisted state synchronously so the first render knows there is stored auth.
// This prevents a flash-redirect to /login on page refresh.
const _storedState = (() => {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem('zonex-store');
    return raw ? (JSON.parse(raw)?.state ?? null) : null;
  } catch {
    return null;
  }
})();

export interface Account {
  id: string;
  account_id: string;
  login: string;
  name: string;
  type: 'live' | 'demo';
  currency: string;
  balance: number;
  equity: number;
  margin: number;
  freeMargin: number;
  marginLevel: number;
  leverage: string;
  server: string;
  isActive: boolean;
  wt_url?: string;
  wt_redirect?: string;
}

export interface Notification {
  id: string;
  type: 'info' | 'success' | 'warning' | 'error';
  title: string;
  message: string;
  read: boolean;
  createdAt: string;
}

export interface BrandingConfig {
  logo: string;
  primaryColor: string;
  companyName: string;
  supportEmail: string;
  theme: 'dark' | 'light';
}

export interface ToastState {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  message: string;
  duration?: number;
}

interface GlobalStore {
  // Auth
  isAuthenticated: boolean;
  /** True from first render until Zustand persist finishes rehydrating; prevents redirect flash on page refresh */
  isBootstrapping: boolean;
  accessToken: string | null;
  user: {
    id: string;
    name: string;
    email: string;
    phone: string;
    country: string;
    kycStatus: 'not_submitted' | 'pending' | 'verified' | 'rejected';
    twoFAEnabled: boolean;
    avatar: string;
  } | null;

  // Accounts
  accounts: Account[];
  activeAccount: Account | null;

  // Branding
  branding: BrandingConfig;

  // UI
  theme: 'dark' | 'light';
  language: string;
  isRTL: boolean;
  notifications: Notification[];
  unreadCount: number;
  toasts: ToastState[];

  // Actions
  setBootstrapping: (val: boolean) => void;
  login: (user: GlobalStore['user'], token: string, accounts: Account[]) => void;
  logout: () => void;
  switchAccount: (accountId: string) => void;
  setTheme: (theme: 'dark' | 'light') => void;
  setLanguage: (lang: string) => void;
  addToast: (toast: Omit<ToastState, 'id'>) => void;
  removeToast: (id: string) => void;
  markNotificationsRead: () => void;
  setNotifications: (notifications: Notification[]) => void;
  updateBranding: (config: Partial<BrandingConfig>) => void;
  updateUser: (data: Partial<NonNullable<GlobalStore['user']>>) => void;
  /** WS-driven: update a single account's financial fields */
  updateAccount: (
    accountId: string,
    patch: Partial<Pick<Account, 'balance' | 'equity' | 'margin' | 'freeMargin' | 'marginLevel'>>
  ) => void;
  /** WS-driven: prepend a new notification and increment unread count */
  addNotification: (notification: Notification) => void;
}

const RTL_LANGS = ['ar', 'he'];

export const useGlobalStore = create<GlobalStore>()(
  persist(
    (set, get) => ({
      isAuthenticated: false,
      isBootstrapping: !!(_storedState?.isAuthenticated && _storedState?.accessToken),
      accessToken: null,
      user: null,
      accounts: [],
      activeAccount: null,
      branding: {
        logo: '/assets/images/app_logo.png',
        primaryColor: '#F59E0B',
        companyName: getDesignTitle(),
        supportEmail: getDesignSupportEmail(),
        theme: 'light',
      },
      theme: 'light',
      language: 'en',
      isRTL: false,
      notifications: [
        {
          id: 'notif-001',
          type: 'success',
          title: 'Deposit Confirmed',
          message: 'Your deposit of $500.00 has been processed successfully.',
          read: false,
          createdAt: '2026-07-15T20:30:00Z',
        },
        {
          id: 'notif-002',
          type: 'warning',
          title: 'Margin Warning',
          message: 'Your margin level on account #48291 has dropped below 150%.',
          read: false,
          createdAt: '2026-07-15T18:15:00Z',
        },
        {
          id: 'notif-003',
          type: 'info',
          title: 'Market Update',
          message: 'EUR/USD volatility increasing ahead of ECB announcement.',
          read: true,
          createdAt: '2026-07-15T14:00:00Z',
        },
        {
          id: 'notif-004',
          type: 'error',
          title: 'Withdrawal Pending',
          message: 'Your withdrawal request requires additional KYC documents.',
          read: false,
          createdAt: '2026-07-14T09:45:00Z',
        },
      ],
      unreadCount: 3,
      toasts: [],

      setBootstrapping: (val) => set({ isBootstrapping: val }),

      login: (user, token, accounts) => {
        const active = accounts.find((a) => a.isActive) || accounts[0];
        // Sync to localStorage for API headers and trading-platform URL
        if (typeof localStorage !== 'undefined') {
          if (token) localStorage.setItem('access_token', token);
          if (active?.account_id) localStorage.setItem('account_id', active.account_id);
          const tradeUrl = active?.wt_url || active?.wt_redirect;
          if (tradeUrl) localStorage.setItem('wt_url', tradeUrl);
        }
        set({ isAuthenticated: true, accessToken: token, user, accounts, activeAccount: active });
      },

      logout: () => {
        // Clear localStorage auth keys
        if (typeof localStorage !== 'undefined') {
          localStorage.removeItem('access_token');
          localStorage.removeItem('account_id');
          localStorage.removeItem('type');
          localStorage.removeItem('accounts');
          localStorage.removeItem('user_info');
          localStorage.removeItem('socket_status');
          localStorage.removeItem('wt_url');
        }
        set({
          isAuthenticated: false,
          accessToken: null,
          user: null,
          accounts: [],
          activeAccount: null,
        });
      },

      switchAccount: (accountId) => {
        const { accounts } = get();
        const updated = accounts.map((a) => ({ ...a, isActive: a.account_id === accountId }));
        const active = updated.find((a) => a.account_id === accountId) || null;
        // Sync account_id and trading-platform URL to localStorage for API headers
        if (typeof localStorage !== 'undefined' && active?.account_id) {
          localStorage.setItem('account_id', active.account_id);
          const tradeUrl = active?.wt_url || active?.wt_redirect;
          if (tradeUrl) localStorage.setItem('wt_url', tradeUrl);
        }
        set({ accounts: updated, activeAccount: active });
        // Dispatch account-changed event for WS re-subscription
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new Event('account-changed'));
        }
      },

      setTheme: (theme) => {
        set({ theme });
        if (typeof document !== 'undefined') {
          if (theme === 'light') {
            document.documentElement.setAttribute('data-theme', 'light');
          } else {
            document.documentElement.removeAttribute('data-theme');
          }
        }
      },

      setLanguage: (lang) => {
        const isRTL = RTL_LANGS.includes(lang);
        set({ language: lang, isRTL });
        if (typeof document !== 'undefined') {
          document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
          document.documentElement.lang = lang;
        }
      },

      addToast: (toast) => {
        const id = `toast-${Date.now()}`;
        set((state) => ({ toasts: [...state.toasts, { ...toast, id }] }));
        setTimeout(() => get().removeToast(id), toast.duration || 3500);
      },

      removeToast: (id) => {
        set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) }));
      },

      markNotificationsRead: () => {
        set((state) => ({
          notifications: state.notifications.map((n) => ({ ...n, read: true })),
          unreadCount: 0,
        }));
      },

      updateBranding: (config) => {
        set((state) => ({ branding: { ...state.branding, ...config } }));
      },

      updateUser: (data) => {
        set((state) => ({
          user: state.user ? { ...state.user, ...data } : null,
        }));
      },

      updateAccount: (accountId, patch) => {
        set((state) => {
          const updatedAccounts = state.accounts.map((a) =>
            a.account_id === accountId ? { ...a, ...patch } : a
          );
          const updatedActive =
            state.activeAccount?.account_id === accountId
              ? { ...state.activeAccount, ...patch }
              : state.activeAccount;
          return { accounts: updatedAccounts, activeAccount: updatedActive };
        });
      },

      addNotification: (notification) => {
        set((state) => {
          if (state.notifications.some((n) => n.id === notification.id)) {
            return state;
          }
          return {
            notifications: [notification, ...state.notifications].slice(0, 50),
            unreadCount: state.unreadCount + (notification.read ? 0 : 1),
          };
        });
      },

      setNotifications: (notifications) => {
        set({
          notifications: notifications.slice(0, 50),
          unreadCount: notifications.filter((n) => !n.read).length,
        });
      },
    }),
    {
      name: 'zonex-store',
      onRehydrateStorage: () => (state) => {
        if (state) state.setBootstrapping(false);
      },
      partialize: (state) => ({
        isAuthenticated: state.isAuthenticated,
        accessToken: state.accessToken,
        user: state.user,
        accounts: state.accounts,
        activeAccount: state.activeAccount,
        theme: state.theme,
        language: state.language,
        isRTL: state.isRTL,
      }),
    }
  )
);
